import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";

import * as firebase from "firebase/app";
import "firebase/firestore";
import * as _ from "lodash";

import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import { Finance } from "../models/finance.model";

@Injectable({
  providedIn: "root",
})
export class FinanceService {
  visibility = environment.visibility;
  userLogin: User;
  private dbPath = "/shops";

  collRef: AngularFirestoreCollection<Finance>;

  constructor(private db: AngularFirestore, private userStore: UserService) {
    this.userStore.getCurrentUser().subscribe((user: User) => {
      if (user) {
        this.userLogin = user;
        this.visibility = user.companyId
          ? user.companyId
          : environment.visibility;
      }
    });
  }

  getFinances(
    shopId: string,
    fromDate: Date,
    toDate: Date
  ): AngularFirestoreCollection<Finance> {
    return this.db.collection(this.dbPath + `/${shopId}/finances`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "asc")
    );
  }

  getFinancesByCustomer(
    shopId: string,
    customerId: string,
    fromDate?: Date,
    toDate?: Date
  ): AngularFirestoreCollection<Finance> {
    fromDate = fromDate ? fromDate : new Date();
    toDate = toDate ? toDate : new Date();

    return this.db.collection(this.dbPath + `/${shopId}/finances`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("customerId", "==", customerId)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "asc")
    );
  }

  getFinancesByCustomerDept(
    shopId: string,
    customerId: string,
    fromDate?: Date,
    toDate?: Date
  ) {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/finances`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("customerId", "==", customerId)
      .where("issueDate", ">=", fromDate)
      .where("issueDate", "<=", toDate)
      .orderBy("issueDate", "asc");

    return ref.get();
  }

  getFinancesBySupplier(
    shopId: string,
    supplierId: string,
    fromDate: Date,
    toDate: Date
  ): AngularFirestoreCollection<Finance> {
    return this.db.collection(this.dbPath + `/${shopId}/finances`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("supplierId", "==", supplierId)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "asc")
    );
  }

  getFinancesByIssueDate(
    shopId: string,
    type: string,
    issueDate: Date
  ): AngularFirestoreCollection<Finance> {
    return this.db.collection(this.dbPath + `/${shopId}/finances`, (ref) => {
      let query: CollectionReference | Query = ref;
      let year = issueDate.getFullYear();
      let month = issueDate.getMonth() + 1;
      let day = issueDate.getDate();
      console.log(`getFinancesByIssueDate: ${day}/${month}/${year}`);

      query = query.where("isDeleted", "==", false);
      query = query.where("isActive", "==", true);
      query = query.where("issueDateYear", "==", year);
      query = query.where("issueDateMonth", "==", month);

      if (type === "day") {
        query = query.where("issueDateDay", "==", day);
      }

      query = query.orderBy("issueDate", "desc");

      return query;
    });
  }

  get(shopId: string, id: string) {
    return this.db
      .collection(this.dbPath + `/${shopId}/finances`)
      .doc(id)
      .valueChanges();
  }

  getFinance(shopId: string, id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath + `/${shopId}/finances`).doc(id);

    return ref.get();
  }

  create(finance: Finance): any {
    let obj = _.clone(finance);
    delete obj.id;

    obj.issueDate = obj.issueDate ? new Date(obj.issueDate) : new Date();
    obj.issueDateYear = obj.issueDate.getFullYear();
    obj.issueDateMonth = obj.issueDate.getMonth() + 1;
    obj.issueDateDay = obj.issueDate.getDate();
    obj.issueDateWeekDay = obj.issueDate.getDay();
    obj.issueDateHours = obj.issueDate.getHours();
    obj.issueDateMinutes = obj.issueDate.getMinutes();
    obj.issueDateSeconds = obj.issueDate.getSeconds();

    obj.groupId = obj.groupId ? obj.groupId : "";
    obj.groupName = obj.groupName ? obj.groupName : "";

    obj.isPrint = !!obj.isPrint;
    obj.isBankTransfer = !!obj.isBankTransfer;

    obj.isActive = true;
    obj.isDeleted = false;
    obj.createdOn = new Date();
    obj.createdBy = this.userLogin.uid;
    obj.updatedOn = new Date();
    obj.updatedBy = this.userLogin.uid;
    obj.author = this.userLogin;
    obj.visibility = this.visibility;
    obj.timestamp = new Date().getTime();

    obj.shopId = obj.shopId ? obj.shopId : this.userLogin.shopId;
    this.collRef = this.db.collection(
      this.dbPath + `/${finance.shopId}/finances`
    );

    console.log("create finance:", obj);

    return this.collRef.add({ ...obj }).then((docRef) => {
      finance.id = docRef.id;
      finance.author = this.userLogin;
      return finance;
    });
  }

  update(finance: Finance): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    finance.issueDate = finance.issueDate ? finance.issueDate : new Date();
    finance.issueDateYear = finance.issueDate.getFullYear();
    finance.issueDateMonth = finance.issueDate.getMonth() + 1;
    finance.issueDateDay = finance.issueDate.getDate();
    finance.issueDateWeekDay = finance.issueDate.getDay();
    finance.issueDateHours = finance.issueDate.getHours();
    finance.issueDateMinutes = finance.issueDate.getMinutes();
    finance.issueDateSeconds = finance.issueDate.getSeconds();

    this.collRef = this.db.collection(
      this.dbPath + `/${finance.shopId}/finances`
    );

    console.log("financeService.update finance:", finance);
    return this.collRef.doc(finance.id).update({
      issueDate: finance.issueDate,
      issueDateSeconds: finance.issueDateSeconds,
      issueDateMinutes: finance.issueDateMinutes,
      issueDateHours: finance.issueDateHours,
      issueDateDay: finance.issueDateDay,
      issueDateWeekDay: finance.issueDateWeekDay,
      issueDateMonth: finance.issueDateMonth,
      issueDateYear: finance.issueDateYear,
      code: finance.code ? finance.code : "",
      note: finance.note ? finance.note : "",
      tags: finance.tags ? finance.tags : [],

      personId: finance.personId ? finance.personId : "",
      personName: finance.personName ? finance.personName : "",
      personMobile: finance.personMobile ? finance.personMobile : "",
      personAddress: finance.personAddress ? finance.personAddress : "",

      groupId: finance.groupId ? finance.groupId : "",
      groupName: finance.groupName ? finance.groupName : "",

      isReceive: finance.isReceive,
      receiveAmount: finance.isReceive ? finance.receiveAmount : 0,
      issueAmount: !finance.isReceive ? finance.issueAmount : 0,

      isPrint: !!finance.isPrint,
      isBankTransfer: !!finance.isBankTransfer,

      customerId: finance.customerId ? finance.customerId : "",
      customerName: finance.customerName ? finance.customerName : "",
      supplierId: finance.supplierId ? finance.supplierId : "",
      supplierName: finance.supplierName ? finance.supplierName : "",
      employeeId: finance.employeeId ? finance.employeeId : "",
      employeeName: finance.employeeName ? finance.employeeName : "",
      objectType: finance.objectType ? finance.objectType : "",
      objectName: finance.objectName ? finance.objectName : "",

      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  changeCustomer(finance: Finance): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.collRef = this.db.collection(
      this.dbPath + `/${finance.shopId}/finances`
    );

    console.log("financeService.changeCustomer:", finance);
    return this.collRef.doc(finance.id).update({
      customerId: finance.customerId ? finance.customerId : "",
      customerName: finance.customerName ? finance.customerName : "",
      objectType: finance.objectType ? finance.objectType : "",
      objectName: finance.objectName ? finance.objectName : "",
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  delete(shopId: string, id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    this.collRef = this.db.collection(this.dbPath + `/${shopId}/finances`);
    return this.collRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  updatePrint(shopId: string, id: string, noPrint: number): Promise<void> {
    console.log("updatePrint", id);
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    this.collRef = this.db.collection(this.dbPath + `/${shopId}/finances`);
    return this.collRef.doc(id).update({
      isPrint: true,
      noPrint: noPrint,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }
}
